import {
  Box,
  Button,
  HStack,
  ListItem,
  ModalBody,
  ModalContent,
  Text,
  UnorderedList,
  useToast,
  VStack
} from '@chakra-ui/react'
import React, { useEffect, useState } from 'react'
import {
  useClinicControllerGetDocumentReferences,
  usePatientControllerSendIntakeDocuments
} from '~/clinician-api'
import type {
  DocumentReferenceDto,
  SendIntakeDocumentsRequestDto
} from '~/clinician-api/models'
import { EventModalHeader } from './event-modal-header'

interface IntakeDocument {
  id: string
  name: string
  category: 'documents_for_review' | 'forms_assessments'
}

interface SendIntakeModalProps {
  patientId: string
  clinicId: string
  onClose: () => void
  onSendDocuments: () => void
  onNotNow: () => void
}

export const SendIntakeModal: React.FC<SendIntakeModalProps> = ({
  patientId,
  clinicId,
  onClose,
  onSendDocuments,
  onNotNow
}) => {
  const toast = useToast()
  const [documentsForReview, setDocumentsForReview] = useState<
    IntakeDocument[]
  >([])
  const [formsAssessments, setFormsAssessments] = useState<IntakeDocument[]>([])

  // Fetch documents from the clinic endpoint using generated API
  const { data: documentReferences, isLoading } =
    useClinicControllerGetDocumentReferences(
      clinicId,
      { categoryCode: 'ClinicPrivacyPolicy' },
      {
        query: {
          enabled: !!clinicId,
          onSuccess: (data: DocumentReferenceDto[]) => {
            // Map the DocumentReferenceDto to IntakeDocument format
            // Since the API only returns ClinicPrivacyPolicy documents, we'll treat them as documents_for_review
            const documentsForReviewData = data.map(
              (doc: DocumentReferenceDto) => ({
                id: doc.id,
                name: doc.description || doc.fileName, // Use description or fallback to fileName
                category: 'documents_for_review' as const
              })
            )

            setDocumentsForReview(documentsForReviewData)

            // For now, set hardcoded forms & assessments as fallback
            // This would need to be updated when the API supports different categories
            setFormsAssessments([
              {
                id: 'form-1',
                name: 'Basic contact details',
                category: 'forms_assessments'
              },
              {
                id: 'form-2',
                name: 'Payment info',
                category: 'forms_assessments'
              },
              {
                id: 'form-3',
                name: 'Introductory questionnaire',
                category: 'forms_assessments'
              }
            ])
          },
          onError: () => {
            // Fallback to hardcoded documents if API fails
            setDocumentsForReview([
              {
                id: '1',
                name: 'Privacy Practices',
                category: 'documents_for_review'
              },
              {
                id: '2',
                name: 'Privacy Policy / HIPAA Notice',
                category: 'documents_for_review'
              },
              {
                id: '3',
                name: 'Informed Consent',
                category: 'documents_for_review'
              },
              {
                id: '4',
                name: 'Consent to Telehealth',
                category: 'documents_for_review'
              },
              {
                id: '5',
                name: 'Consent to Record',
                category: 'documents_for_review'
              },
              {
                id: '6',
                name: 'Good Faith Estimate',
                category: 'documents_for_review'
              }
            ])
            setFormsAssessments([
              {
                id: '7',
                name: 'Basic contact details',
                category: 'forms_assessments'
              },
              { id: '8', name: 'Payment info', category: 'forms_assessments' },
              {
                id: '9',
                name: 'Introductory questionnaire',
                category: 'forms_assessments'
              }
            ])
          }
        }
      }
    )

  // Send intake documents mutation using generated API
  const sendDocumentsMutation = usePatientControllerSendIntakeDocuments({
    mutation: {
      onSuccess: () => {
        toast({
          title: 'Intake documents sent',
          description: 'The intake documents have been sent to the client.',
          status: 'success',
          duration: 3000,
          isClosable: true
        })
        onSendDocuments()
      },
      onError: (error: any) => {
        toast({
          title: 'Error sending documents',
          description:
            error?.message ||
            'Failed to send intake documents. Please try again.',
          status: 'error',
          duration: 5000,
          isClosable: true
        })
      }
    }
  })

  const handleSendDocuments = () => {
    // Send all documents categorized by type
    const intakePolicyDocuments = documentsForReview.map(doc => doc.name)
    const intakeFormsAssessments = formsAssessments.map(form => form.name)

    const requestData: SendIntakeDocumentsRequestDto = {
      intakePolicyDocuments,
      intakeFormsAssessments
    }

    sendDocumentsMutation.mutate({
      patientId,
      data: requestData
    })
  }

  return (
    <ModalContent maxW="480px">
      <EventModalHeader showCloseButton={true} onClose={onClose}>
        <Text fontSize="18px" fontWeight="600">
          Send intake documents?
        </Text>
      </EventModalHeader>
      <ModalBody pb={6}>
        <VStack align="stretch" spacing={6}>
          {/* Description */}
          <Text fontSize="14px" color="gray.700">
            Blueprint automates intake delivery to streamline prep and reduce
            admin work.
          </Text>

          {/* Documents for review section */}
          <Box>
            <Text fontSize="12px" fontWeight="500" mb={3} color="gray.600">
              Documents for review ({documentsForReview.length})
            </Text>
            <UnorderedList spacing={2} pl={4}>
              {documentsForReview.map(doc => (
                <ListItem key={doc.id} fontSize="14px">
                  {doc.name}
                </ListItem>
              ))}
            </UnorderedList>
          </Box>

          {/* Forms & Assessments section */}
          <Box>
            <Text fontSize="12px" fontWeight="500" mb={3} color="gray.600">
              Forms & Assessments ({formsAssessments.length})
            </Text>
            <UnorderedList spacing={2} pl={4}>
              {formsAssessments.map(form => (
                <ListItem key={form.id} fontSize="14px">
                  {form.name}
                </ListItem>
              ))}
            </UnorderedList>
          </Box>

          {/* Action buttons */}
          <HStack spacing={3} pt={4}>
            <Button
              variant="ghost"
              onClick={onNotNow}
              flex={1}
              borderRadius="8px"
              fontSize="14px"
              fontWeight="500"
              color="gray.700"
              _hover={{ bg: 'gray.50' }}
            >
              Not now
            </Button>
            <Button
              onClick={handleSendDocuments}
              flex={1}
              borderRadius="8px"
              fontSize="14px"
              fontWeight="500"
              colorScheme="blue"
              bg="blue.500"
              color="white"
              _hover={{ bg: 'blue.600' }}
              isLoading={sendDocumentsMutation.isLoading}
              loadingText="Sending..."
            >
              Send documents
            </Button>
          </HStack>
        </VStack>
      </ModalBody>
    </ModalContent>
  )
}
